import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Space, Typography, Divider, Row, Col, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, SwapOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const RenameColumnNodeProperties = ({ config, form, inputSchema }) => {
  const [columnMappings, setColumnMappings] = useState(config.columnMappings || []);

  // Update form when columnMappings change
  useEffect(() => {
    form.setFieldsValue({
      columnMappings: columnMappings
    });
  }, [columnMappings, form]);

  // Add new column mapping
  const addColumnMapping = () => {
    const newMapping = {
      id: `mapping_${Date.now()}`,
      oldName: '',
      newName: ''
    };
    setColumnMappings([...columnMappings, newMapping]);
  };

  // Remove column mapping
  const removeColumnMapping = (index) => {
    const newMappings = columnMappings.filter((_, i) => i !== index);
    setColumnMappings(newMappings);
  };

  // Update column mapping
  const updateColumnMapping = (index, updates) => {
    const newMappings = [...columnMappings];
    newMappings[index] = { ...newMappings[index], ...updates };
    setColumnMappings(newMappings);
  };

  // Swap old and new names
  const swapNames = (index) => {
    const mapping = columnMappings[index];
    updateColumnMapping(index, {
      oldName: mapping.newName,
      newName: mapping.oldName
    });
  };

  // Common rename patterns
  const commonRenames = [
    { from: 'id', to: 'user_id' },
    { from: 'name', to: 'full_name' },
    { from: 'email', to: 'email_address' },
    { from: 'phone', to: 'phone_number' },
    { from: 'date', to: 'created_date' },
    { from: 'amount', to: 'total_amount' }
  ];

  const applyCommonRename = (pattern) => {
    const newMapping = {
      id: `mapping_${Date.now()}`,
      oldName: pattern.from,
      newName: pattern.to
    };
    setColumnMappings([...columnMappings, newMapping]);
  };

  return (
    <div>
      <Form.Item label="Description" name="description" initialValue={config.description || 'Rename column headers for better readability'}>
        <TextArea rows={2} placeholder="Describe the column renaming purpose" />
      </Form.Item>

      <Divider orientation="left">Column Rename Mappings</Divider>
      
      {/* Hidden form field to store columnMappings in form */}
      <Form.Item name="columnMappings" initialValue={columnMappings} style={{ display: 'none' }}>
        <Input />
      </Form.Item>

      {/* Column Mappings List */}
      <div style={{ marginBottom: 16 }}>
        {columnMappings.map((mapping, index) => (
          <Card 
            key={mapping.id}
            size="small" 
            style={{ marginBottom: 12 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>Mapping {index + 1}</Text>
                <Space>
                  <Button 
                    type="text" 
                    size="small"
                    icon={<SwapOutlined />}
                    onClick={() => swapNames(index)}
                    title="Swap old and new names"
                  />
                  <Button 
                    type="text" 
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeColumnMapping(index)}
                  />
                </Space>
              </div>
            }
          >
            <Row gutter={12}>
              <Col span={11}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Current Name</Text>
                  {inputSchema && inputSchema.length > 0 ? (
                    <Select
                      size="small"
                      value={mapping.oldName}
                      onChange={(value) => updateColumnMapping(index, { oldName: value })}
                      placeholder="Select column"
                      showSearch
                      style={{ width: '100%' }}
                    >
                      {inputSchema.map(col => (
                        <Select.Option key={col.name} value={col.name}>{col.name}</Select.Option>
                      ))}
                    </Select>
                  ) : (
                    <Input 
                      size="small"
                      value={mapping.oldName}
                      onChange={(e) => updateColumnMapping(index, { oldName: e.target.value })}
                      placeholder="old_column_name"
                    />
                  )}
                </div>
              </Col>
              <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '20px' }}>
                <Text type="secondary" style={{ fontSize: '16px' }}>→</Text>
              </Col>
              <Col span={11}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>New Name</Text>
                  <Input 
                    size="small"
                    value={mapping.newName}
                    onChange={(e) => updateColumnMapping(index, { newName: e.target.value })}
                    placeholder="new_column_name"
                  />
                </div>
              </Col>
            </Row>

            {/* Validation */}
            {mapping.oldName && mapping.newName && mapping.oldName === mapping.newName && (
              <div style={{ marginTop: 8 }}>
                <Text type="warning" style={{ fontSize: '11px' }}>
                  ⚠️ Old and new names are identical
                </Text>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Add New Mapping Button */}
      <Button 
        type="dashed" 
        onClick={addColumnMapping}
        block
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
      >
        Add Column Rename
      </Button>

      {/* Quick Add Common Renames */}
      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 8 }}>
          Quick Add Common Renames:
        </Text>
        <Space wrap>
          {commonRenames.map((pattern, index) => (
            <Button
              key={index}
              size="small"
              type="dashed"
              style={{ fontSize: '10px', height: '24px' }}
              onClick={() => applyCommonRename(pattern)}
            >
              {pattern.from} → {pattern.to}
            </Button>
          ))}
        </Space>
      </div>

      {/* Options */}
      <Divider orientation="left" style={{ fontSize: '12px' }}>Options</Divider>

      <Form.Item 
        label="Missing Column Strategy" 
        name="missingStrategy" 
        initialValue={config.missingStrategy || 'ignore'}
      >
        <Select size="small">
          <Select.Option value="ignore">Ignore if column doesn't exist</Select.Option>
          <Select.Option value="error">Error if column doesn't exist</Select.Option>
          <Select.Option value="warn">Warn if column doesn't exist</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item 
        label="Duplicate Name Strategy" 
        name="duplicateStrategy" 
        initialValue={config.duplicateStrategy || 'append_number'}
      >
        <Select size="small">
          <Select.Option value="append_number">Append number (e.g., name_1, name_2)</Select.Option>
          <Select.Option value="error">Error on duplicate names</Select.Option>
          <Select.Option value="overwrite">Overwrite existing columns</Select.Option>
        </Select>
      </Form.Item>

      <Divider orientation="left" style={{ fontSize: '12px' }}>Execution Behavior</Divider>
      
      <div style={{ padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '4px', fontSize: '12px' }}>
        <Text type="secondary">
          <strong>Rename Columns Behavior:</strong><br/>
          • Column headers will be renamed according to mappings<br/>
          • Data content remains unchanged, only headers change<br/>
          • Missing columns handled based on strategy setting<br/>
          • Duplicate names handled based on strategy setting
        </Text>
      </div>

      {/* Summary */}
      {columnMappings.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Text style={{ fontSize: '12px', color: '#666' }}>
            <strong>Summary:</strong> {columnMappings.length} column(s) will be renamed
          </Text>
        </div>
      )}
    </div>
  );
};

export default RenameColumnNodeProperties;